import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Trash2, Eye, ChevronDown, Search, Filter, ArrowLeft, RefreshCw, Mail, Phone, MapPin, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import { getToken, getUser } from '../utils/auth';

export default function AdminPublicApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const token = getToken();
    const user = getUser();
    if (!token || !user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchApplications();
  }, [navigate]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/public-applications');
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      await api.put(`/admin/public-applications/${appId}/status?status=${newStatus}`);
      setApplications(apps => 
        apps.map(app => app.id === appId ? { ...app, status: newStatus } : app)
      );
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const downloadResume = async (appId, filename) => {
    try {
      const response = await api.get(`/admin/public-applications/${appId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `resume_${appId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download resume');
    }
  };

  const deleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await api.delete(`/admin/public-applications/${appId}`);
      setApplications(apps => apps.filter(app => app.id !== appId));
      toast.success('Application deleted');
      if (selectedApp?.id === appId) {
        setShowDetails(false);
        setSelectedApp(null);
      }
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.job_title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'reviewed': return 'bg-yellow-100 text-yellow-700';
      case 'contacted': return 'bg-purple-100 text-purple-700';
      case 'hired': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-public-applications-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/admin')}
                className="text-zinc-600 hover:text-zinc-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-zinc-900">Public Applications</h1>
                <p className="text-sm text-zinc-500">{applications.length} total applications</p>
              </div>
            </div>
            <Button onClick={fetchApplications} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-zinc-100 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                type="text"
                placeholder="Search by name, email, or job title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="relative">
              <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-8 py-2 border border-zinc-200 rounded-lg text-sm appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
                data-testid="status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="new">New</option>
                <option value="reviewed">Reviewed</option>
                <option value="contacted">Contacted</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-brand-blue border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-zinc-500">Loading applications...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-zinc-100">
            <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">No applications found</h3>
            <p className="text-zinc-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your filters' 
                : 'Applications will appear here when candidates apply'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Applications List */}
            <div className="lg:col-span-2 space-y-4">
              {filteredApplications.map(app => (
                <div 
                  key={app.id} 
                  className={`bg-white rounded-xl p-5 border transition-all cursor-pointer ${
                    selectedApp?.id === app.id 
                      ? 'border-brand-blue shadow-md' 
                      : 'border-zinc-100 hover:border-zinc-200 hover:shadow-sm'
                  }`}
                  onClick={() => {
                    setSelectedApp(app);
                    setShowDetails(true);
                  }}
                  data-testid={`application-card-${app.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-zinc-900">{app.full_name}</h3>
                      <p className="text-sm text-zinc-500">{app.email}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(app.status)}`}>
                      {app.status || 'new'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-zinc-600 mb-3">
                    {app.job_title && (
                      <div className="flex items-center gap-1">
                        <Briefcase className="w-3.5 h-3.5" />
                        <span className="truncate max-w-[200px]">{app.job_title}</span>
                      </div>
                    )}
                    {app.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" />
                        <span>{app.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-400">
                      Applied {formatDate(app.submitted_at)}
                    </p>
                    <div className="flex items-center gap-2">
                      {app.resume_filename && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadResume(app.id, app.resume_filename);
                          }}
                          data-testid={`download-resume-${app.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteApplication(app.id);
                        }}
                        data-testid={`delete-app-${app.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Details Panel */}
            <div className="lg:col-span-1">
              {showDetails && selectedApp ? (
                <div className="bg-white rounded-xl border border-zinc-100 sticky top-24">
                  <div className="p-5 border-b border-zinc-100">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-zinc-900">Application Details</h3>
                      <button 
                        onClick={() => setShowDetails(false)}
                        className="text-zinc-400 hover:text-zinc-600 lg:hidden"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Full Name</p>
                      <p className="font-medium text-zinc-900">{selectedApp.full_name}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Email</p>
                      <a href={`mailto:${selectedApp.email}`} className="font-medium text-brand-blue hover:underline flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />
                        {selectedApp.email}
                      </a>
                    </div>
                    
                    {selectedApp.phone && (
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Phone</p>
                        <a href={`tel:${selectedApp.phone}`} className="font-medium text-zinc-900 flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          {selectedApp.phone}
                        </a>
                      </div>
                    )}
                    
                    {selectedApp.address && (
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Address</p>
                        <p className="font-medium text-zinc-900 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {selectedApp.address}
                        </p>
                      </div>
                    )}
                    
                    {selectedApp.job_title && (
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Applied For</p>
                        <p className="font-medium text-zinc-900">{selectedApp.job_title}</p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Status</p>
                      <select
                        value={selectedApp.status || 'new'}
                        onChange={(e) => updateStatus(selectedApp.id, e.target.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border-0 ${getStatusColor(selectedApp.status || 'new')}`}
                        data-testid="status-select"
                      >
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="contacted">Contacted</option>
                        <option value="hired">Hired</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Applied On</p>
                      <p className="font-medium text-zinc-900">{formatDate(selectedApp.submitted_at)}</p>
                    </div>

                    {selectedApp.resume_filename && (
                      <Button 
                        className="w-full mt-4"
                        onClick={() => downloadResume(selectedApp.id, selectedApp.resume_filename)}
                        data-testid="download-resume-detail"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Resume
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-zinc-100 p-8 text-center">
                  <Eye className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">Select an application to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
